import apiClient from "./client";

// Download all photos for a finding
export function getPhotosByFindingId(findingId) {
  return apiClient
    .get(`/photoitems/byFinding/${encodeURIComponent(findingId)}`)
    .then((res) => res.data);
}

export function getPhotosByFindingIdResized(findingId) {
  return apiClient
    .get(`/photoitems/byFindingResized/${encodeURIComponent(findingId)}`)
    .then((res) => res.data);
}

// Get all photos for an asset
export function getPhotosByAssetId(assetId) {
  return apiClient
    .get(`/photoitems/byAsset/${assetId}`)
    .then((res) => res.data);
}

// Get all photos for a task
export function getPhotosByTaskId(taskId) {
  return apiClient
    .get(`/photoitems/byTask/${encodeURIComponent(taskId)}`)
    .then((res) => res.data);
}

// Create photo metadata (after Azure upload)
export function createPhotoItem(photoItem) {
  return apiClient
    .post("/photoitems/create", photoItem)
    .then((res) => res.data);
}

// Update a photo metadata record
export function updatePhotoItem(photoUrlId, photoItem) {
  return apiClient
    .put(`/photoitems/${encodeURIComponent(photoUrlId)}`, photoItem)
    .then((res) => res.data);
}

// Delete a photo item
export function deletePhotoItem(photoUrlId) {
  return apiClient
    .delete(`/photoitems/${encodeURIComponent(photoUrlId)}`)
    .then((res) => res.data);
}

// ── iOS-safe photo snapshot helpers ─────────────────────────────────────────
// All photo-input screens MUST go through these so behaviour stays identical
// across Finding / Task / Asset / etc. on iPhone.
//
// Why: on iOS WebKit (Safari and any browser, since they're all WebKit), the
// camera-returned File points at a transient temp file. If the page is
// backgrounded between picking and submitting (e.g. user fills out a form for
// a few minutes), iOS can evict the bytes — the File reference stays valid
// but reading it yields an empty body and the upload silently fails.
//
// Mitigation: as soon as the @change event fires, copy the bytes into a
// memory-backed Blob. The Blob lives in the JS heap, so iOS can't evict it.
// We also mint the preview Object URL here, once, so re-renders don't leak
// fresh blob URLs every tick.
export async function snapshotPhotoFile(file) {
  if (!file) return null;
  try {
    const buf = await file.arrayBuffer();
    const blob = new Blob([buf], { type: file.type || "image/jpeg" });
    const name = file.name || "photo.jpg";
    // fileRef is a stable File wrapper around the memory-backed Blob — used
    // as the identity token in v-file-input's v-model so we can tell apart
    // "user added a new file" from "user removed an existing chip" without
    // re-snapshotting bytes on every render.
    const fileRef = new File([blob], name, { type: blob.type });
    return {
      blob,
      name,
      type: blob.type,
      size: blob.size,
      previewUrl: URL.createObjectURL(blob),
      fileRef,
    };
  } catch {
    return null;
  }
}

function _normalizeFileInput(input) {
  if (Array.isArray(input)) return input;
  if (input instanceof FileList) return Array.from(input);
  if (input) return [input];
  return [];
}

export async function snapshotPhotoFiles(input) {
  const list = _normalizeFileInput(input);
  const snaps = await Promise.all(list.map(snapshotPhotoFile));
  return snaps.filter(Boolean);
}

export function revokePhotoSnapshot(snap) {
  if (snap?.previewUrl) {
    try { URL.revokeObjectURL(snap.previewUrl); } catch {}
  }
}

export function revokePhotoSnapshots(snaps) {
  (snaps || []).forEach(revokePhotoSnapshot);
}

// Returns the File[] suitable for v-file-input's :model-value. The same File
// objects are returned across calls (we cached them in the snapshot itself),
// so v-file-input doesn't see "everything changed" on every render.
export function snapshotsToFiles(snaps) {
  return (snaps || []).map(s => s.fileRef);
}

// Diffs an incoming File[] from v-file-input against the current snapshots:
//   - existing files (identified by fileRef identity) → keep snapshot as-is
//   - new files (from the OS picker) → read bytes to memory and create a snapshot
//   - missing files (user removed a chip) → revoke their preview URL and drop
// Returns the next snapshot array. Use as:
//   this.photoSnapshots = await reconcilePhotoSnapshots(this.photoSnapshots, files);
export async function reconcilePhotoSnapshots(currentSnaps, incoming) {
  const list = _normalizeFileInput(incoming);
  const keep = (currentSnaps || []).filter(s => list.includes(s.fileRef));
  const newFiles = list.filter(f => !keep.some(s => s.fileRef === f) && f?.type?.startsWith("image/"));
  const newSnaps = await snapshotPhotoFiles(newFiles);
  const dropped = (currentSnaps || []).filter(s => !keep.includes(s));
  revokePhotoSnapshots(dropped);
  return [...keep, ...newSnaps];
}

export function uploadPhoto(photoItem) {
  // Validate required fields - need either FindingId or TaskId
  if (!photoItem.Blob) {
    throw new Error("File (Blob) is required for photo upload");
  }
  if (!photoItem.PhotoUrlId) {
    throw new Error("PhotoUrlId is required for photo upload");
  }
  if (!photoItem.FindingId && !photoItem.TaskId && !photoItem.AssetId) {
    throw new Error("Either FindingId, TaskId or AssetId is required for photo upload");
  }

  const formData = new FormData();
  formData.append("file", photoItem.Blob); // Blob/File
  formData.append("PhotoUrlId", photoItem.PhotoUrlId);
  if (photoItem.FindingId) formData.append("FindingId", photoItem.FindingId);
  if (photoItem.TaskId) formData.append("TaskId", photoItem.TaskId);
  if (photoItem.AssetId) formData.append("AssetId", photoItem.AssetId);
  
  // Format CreatedAt for .NET DateTime parsing
  // .NET expects ISO 8601 format: "2024-01-01T12:00:00" or "2024-01-01T12:00:00Z"
  const createdAt = photoItem.CreatedAt instanceof Date 
    ? photoItem.CreatedAt.toISOString() 
    : photoItem.CreatedAt;
  formData.append("CreatedAt", createdAt);

  // Axios will automatically set Content-Type with boundary for FormData
  // We need to explicitly remove the default Content-Type header so axios can set it automatically
  return apiClient.post("/photoitems/upload", formData, {
    transformRequest: (data, headers) => {
      // Delete Content-Type header to let browser/axios set it with boundary for FormData
      // This is necessary because the default axios client has Content-Type: application/json
      delete headers["Content-Type"];
      return data;
    },
  });
}
